import { loadSettings, saveSettings, type Settings } from './storage';

class ArcadeAudio {
  ctx: AudioContext | null = null;
  master: GainNode | null = null;
  musicGain: GainNode | null = null;
  sfxGain: GainNode | null = null;
  settings: Settings = loadSettings();
  buffers = new Map<string, AudioBuffer>();
  music: AudioBufferSourceNode | null = null;
  musicName = '';
  unlocked = false;

  private ensure() {
    if (this.ctx) return;
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctx();
    this.master = this.ctx.createGain();
    this.musicGain = this.ctx.createGain();
    this.sfxGain = this.ctx.createGain();
    this.musicGain.connect(this.master);
    this.sfxGain.connect(this.master);
    this.master.connect(this.ctx.destination);
    this.apply();
  }

  async unlock() {
    this.ensure();
    if (!this.ctx) return;
    if (this.ctx.state === 'suspended') await this.ctx.resume();
    this.unlocked = true;
  }

  apply() {
    if (!this.master || !this.musicGain || !this.sfxGain) return;
    const mute = this.settings.muted ? 0 : 1;
    this.master.gain.value = this.settings.volume * mute;
    this.musicGain.gain.value = this.settings.music ? 0.32 : 0;
    this.sfxGain.gain.value = 0.85;
  }

  setMuted(muted: boolean) {
    this.settings.muted = muted;
    saveSettings(this.settings);
    this.apply();
  }

  toggleMute() {
    this.setMuted(!this.settings.muted);
    return this.settings.muted;
  }

  async load(name: string, url: string) {
    if (this.buffers.has(name)) return;
    this.ensure();
    try {
      const res = await fetch(url);
      const buf = await res.arrayBuffer();
      const decoded = await this.ctx!.decodeAudioData(buf);
      this.buffers.set(name, decoded);
    } catch {
      /* missing asset is non-fatal */
    }
  }

  play(name: string, opts: { volume?: number; playbackRate?: number } = {}) {
    if (!this.ctx || !this.sfxGain || this.settings.muted) return;
    const buf = this.buffers.get(name);
    if (!buf) return;
    const src = this.ctx.createBufferSource();
    const g = this.ctx.createGain();
    src.buffer = buf;
    src.playbackRate.value = opts.playbackRate ?? 1;
    g.gain.value = opts.volume ?? 1;
    src.connect(g);
    g.connect(this.sfxGain);
    src.start();
  }

  async playMusic(name: string, url: string) {
    await this.load(name, url);
    if (!this.ctx || !this.musicGain) return;
    if (this.musicName === name && this.music) return;
    this.stopMusic();
    const buf = this.buffers.get(name);
    if (!buf) return;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    src.connect(this.musicGain);
    src.start();
    this.music = src;
    this.musicName = name;
  }

  stopMusic() {
    try {
      this.music?.stop();
    } catch {
      /* already stopped */
    }
    this.music = null;
    this.musicName = '';
  }

  blip(freq = 440, dur = 0.08, type: OscillatorType = 'square', vol = 0.08) {
    if (!this.ctx || !this.sfxGain || this.settings.muted) return;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = type;
    o.frequency.value = freq;
    g.gain.value = vol;
    g.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + dur);
    o.connect(g);
    g.connect(this.sfxGain);
    o.start();
    o.stop(this.ctx.currentTime + dur);
  }
}

export const arcadeAudio = new ArcadeAudio();
